import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { IPendingSocialAuth, SocialAuthCompletion } from '../../types/auth';
import legal from '../../config/legal.json';
import SignupConsent from './SignupConsent';

interface Props {
  pending: IPendingSocialAuth;
  complete: (payload: SocialAuthCompletion) => Promise<void>;
  onSuccess: () => void;
  onCancel: () => void;
}

export default function SocialAccountConfirmation({ pending, complete, onSuccess, onCancel }: Props) {
  const [step, setStep] = useState<'choose' | 'link'>('choose');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [error, setError] = useState('');
  const [expired, setExpired] = useState(() => Date.parse(pending.expiresAt) <= Date.now());
  useEffect(() => {
    setConsent(false);
  }, [pending.pendingToken]);

  useEffect(() => {
    const timer = setTimeout(() => setExpired(true), Math.max(0, Date.parse(pending.expiresAt) - Date.now()));
    return () => clearTimeout(timer);
  }, [pending.expiresAt]);

  const submit = async (action: 'create' | 'link') => {
    if (busyRef.current || expired) return;
    if (action === 'create' && !consent) return;
    busyRef.current = true;
    setBusy(true);
    setError('');
    try {
      await complete(action === 'create'
        ? { pendingToken: pending.pendingToken, action, termsAccepted: consent, termsVersion: legal.version, ageConfirmed: consent }
        : { pendingToken: pending.pendingToken, action, email: email.trim(), password });
      setPassword('');
      onSuccess();
    } catch (err) {
      const failure = err as { message?: string; status?: number; code?: string };
      if (failure.status === 410 || failure.code === 'SOCIAL_PENDING_EXPIRED') setExpired(true);
      setError(failure.message || 'Unable to finish sign-in. Please try again.');
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const providerName = pending.provider === 'apple' ? 'Apple ID' : 'Google account';
  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Typography variant="h6" component="h2">
          No Dawr account is linked to this {providerName}.
        </Typography>
        {expired ? (
          <>
            <Alert severity="warning">This sign-in request has expired. Continue with {pending.provider === 'apple' ? 'Apple' : 'Google'} again to restart.</Alert>
            <Button onClick={onCancel} disabled={busy}>Restart sign-in</Button>
          </>
        ) : (
          <>
            {error && <Alert severity="error">{error}</Alert>}
            {step === 'choose' ? (
              <>
                <Typography variant="body2">
                  Create a separate Dawr account, or sign in with your existing account to connect this sign-in method.
                </Typography>
                <SignupConsent checked={consent} onChange={setConsent} disabled={busy} />
                <Typography variant="caption">{legal.safetyNotice} Legal revision: {legal.version}.</Typography>
                <Button variant="contained" disabled={busy || !consent} onClick={() => void submit('create')}>
                  {busy ? 'Creating account…' : 'Create new account'}
                </Button>
                <Button variant="outlined" disabled={busy} onClick={() => { setError(''); setStep('link'); }}>
                  I already have an account
                </Button>
              </>
            ) : (
              <Box component="form" onSubmit={event => { event.preventDefault(); void submit('link'); }}>
                <Stack spacing={2}>
                  <Typography variant="body2">
                    Sign in with your Dawr email and password to connect {pending.provider === 'apple' ? 'Apple' : 'Google'} to that account.
                  </Typography>
                  <TextField
                    label="Email" type="email" autoComplete="email" required fullWidth
                    value={email} onChange={event => setEmail(event.target.value)} disabled={busy}
                  />
                  <TextField
                    label="Password" type="password" autoComplete="current-password" required fullWidth
                    value={password} onChange={event => setPassword(event.target.value)} disabled={busy}
                  />
                  <Button type="submit" variant="contained" disabled={busy || !email.trim() || !password}>
                    {busy ? 'Connecting…' : 'Sign in and connect'}
                  </Button>
                  <Button disabled={busy} onClick={() => { setPassword(''); setError(''); setStep('choose'); }}>
                    Back
                  </Button>
                </Stack>
              </Box>
            )}
            <Button disabled={busy} onClick={onCancel}>Cancel</Button>
          </>
        )}
      </Stack>
    </Box>
  );
}
